import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile, signInWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';

const UserManagement = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Staff'
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const q = query(collection(db, 'users'), where('role', 'in', ['Staff', 'Admin', 'Barista', 'Roaster']));
        const querySnapshot = await getDocs(q);
        const usersList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersList);
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to fetch users');
      }
    };

    fetchUsers();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'Staff'
    });
    setEditingUser(null);
    setShowModal(false);
    setShowPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (editingUser) {
        // Update existing user (only name for now, as email/password changes require re-authentication)
        await updateDoc(doc(db, 'users', editingUser.id), {
          name: formData.name,
          updatedAt: new Date()
        });
        setSuccess('User updated successfully!');
        setUsers(users.map(user => 
          user.id === editingUser.id ? { ...user, name: formData.name } : user
        ));
      } else {
        // Store admin credentials temporarily
        const adminEmail = currentUser.email;
        
        // Create new user
        const userCredential = await createUserWithEmailAndPassword(
          auth, 
          formData.email, 
          formData.password
        );

        // Update display name
        await updateProfile(userCredential.user, {
          displayName: formData.name
        });

        // Add user to Firestore
        const userData = {
          uid: userCredential.user.uid,
          name: formData.name,
          email: formData.email,
          role: formData.role,
          createdAt: new Date(),
          createdBy: currentUser.uid,
          active: true
        };

        // Add isAdmin flag for Admin role
        if (formData.role === 'Admin') {
          userData.isAdmin = true;
        }

        await addDoc(collection(db, 'users'), userData);

        // Sign out the new user and sign back in as admin
        await auth.signOut();
        
        // Note: This creates a user but requires admin to log back in
        // In production, use Firebase Admin SDK on the backend
        setSuccess(`${formData.role} user created successfully! Please log in again to continue.`);
        
        // Refresh users list
        const q = query(collection(db, 'users'), where('role', 'in', ['Staff', 'Admin', 'Barista', 'Roaster']));
        const querySnapshot = await getDocs(q);
        const usersList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setUsers(usersList);
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('This email is already registered');
      } else if (error.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else {
        setError('Failed to save user');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      name: user.name,
      email: user.email,
      password: '', // Don't populate password for security
      role: user.role || 'Staff'
    });
    setEditingUser(user);
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? They will no longer be able to log in.')) return;

    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsers(users.filter(user => user.id !== userId));
      setSuccess('User deleted successfully!');
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await updateDoc(doc(db, 'users', user.id), {
        active: !user.active,
        updatedAt: new Date()
      });
      setUsers(users.map(u => 
        u.id === user.id ? { ...u, active: !u.active } : u
      ));
      setSuccess(`User ${user.active ? 'deactivated' : 'activated'} successfully!`);
    } catch (error) {
      console.error('Error updating user status:', error);
      setError('Failed to update user status');
    }
  };

  return (
    <div style={{
      background: '#f8f8f8',
      minHeight: '100vh',
      padding: '0',
      fontFamily: 'Inter, Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #ececec',
        padding: isMobile ? '20px' : '32px 40px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? '16px' : '0'
      }}>
        <div>
          <h1 style={{
            fontFamily: 'Inter, Arial, sans-serif',
            fontSize: isMobile ? '24px' : '32px',
            fontWeight: '700',
            color: '#222',
            margin: '0',
            letterSpacing: '1px',
            textTransform: 'uppercase'
          }}>USER MANAGEMENT</h1>
          <div style={{
            fontSize: '14px',
            color: '#666',
            marginTop: '4px',
            fontFamily: 'Georgia, serif'
          }}>
            Manage staff and admin accounts
          </div>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          style={{
            background: '#222',
            color: '#fff',
            border: 'none',
            padding: isMobile ? '12px 20px' : '14px 24px',
            fontSize: '12px',
            fontWeight: '700',
            fontFamily: 'Inter, Arial, sans-serif',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            borderRadius: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: isMobile ? '100%' : 'auto'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#000';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#222';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          <FaPlus /> CREATE USER
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          color: '#991b1b',
          padding: '12px 16px',
          margin: isMobile ? '16px 20px' : '16px 40px',
          fontSize: '12px',
          fontWeight: '500',
          fontFamily: 'Inter, Arial, sans-serif',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{error}</span>
          <button 
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#991b1b',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '0',
              marginLeft: '12px'
            }}
          >×</button>
        </div>
      )}
      {success && (
        <div style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          color: '#16a34a',
          padding: '12px 16px',
          margin: isMobile ? '16px 20px' : '16px 40px',
          fontSize: '12px',
          fontWeight: '500',
          fontFamily: 'Inter, Arial, sans-serif',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{success}</span>
          <button 
            onClick={() => setSuccess(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#16a34a',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '0',
              marginLeft: '12px'
            }}
          >×</button>
        </div>
      )}

      {/* Users List */}
      <div style={{
        padding: isMobile ? '20px' : '40px'
      }}>
        {isMobile ? (
          // Mobile Card View
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {users.map((user) => (
              <div key={user.id} style={{
                background: '#fff',
                border: '1px solid #ececec',
                padding: '16px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px'
                }}>
                  <div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#222',
                      marginBottom: '4px',
                      fontFamily: 'Inter, Arial, sans-serif'
                    }}>{user.name}</div>
                    <div style={{
                      fontSize: '14px',
                      color: '#666',
                      fontFamily: 'Georgia, serif'
                    }}>{user.email}</div>
                  </div>
                  <span style={{
                    padding: '4px 8px',
                    background: user.active !== false ? '#f0fdf4' : '#fee2e2',
                    color: user.active !== false ? '#16a34a' : '#991b1b',
                    fontSize: '11px',
                    fontWeight: '600',
                    textAlign: 'center',
                    fontFamily: 'Inter, Arial, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    border: '1px solid ' + (user.active !== false ? '#bbf7d0' : '#fecaca')
                  }}>
                    {user.active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <div style={{
                  borderTop: '1px solid #f8f8f8',
                  paddingTop: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    fontSize: '12px',
                    color: '#999',
                    marginBottom: '4px',
                    fontFamily: 'Inter, Arial, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>ROLE</div>
                  <div style={{
                    fontSize: '14px',
                    color: '#333',
                    fontFamily: 'Inter, Arial, sans-serif'
                  }}>{user.role || 'Staff'}</div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #f8f8f8',
                  paddingTop: '12px'
                }}>
                  <button 
                    onClick={() => handleToggleStatus(user)}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #ececec',
                      background: '#f8f8f8',
                      color: '#666',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontFamily: 'Inter, Arial, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderRadius: '0'
                    }}
                  >
                    {user.active !== false ? 'DEACTIVATE' : 'ACTIVATE'}
                  </button>
                  <button 
                    onClick={() => handleEdit(user)}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #ececec',
                      background: '#f8f8f8',
                      color: '#666',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontFamily: 'Inter, Arial, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderRadius: '0'
                    }}
                  >
                    EDIT
                  </button>
                  <button 
                    onClick={() => handleDelete(user.id)}
                    style={{
                      padding: '8px 16px',
                      border: '1px solid #ececec',
                      background: '#f8f8f8',
                      color: '#dc2626',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontFamily: 'Inter, Arial, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderRadius: '0'
                    }}
                  >
                    DELETE
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Desktop Table View
          <div style={{
            background: '#fff',
            border: '1px solid #ececec',
            overflow: 'hidden'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{
                  background: '#f8f8f8',
                  borderBottom: '1px solid #ececec'
                }}>
                  <th style={{
                    padding: '16px 24px',
                    textAlign: 'left',
                    fontSize: '10px',
                    fontWeight: '700',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontFamily: 'Inter, Arial, sans-serif'
                  }}>NAME</th>
                  <th style={{
                    padding: '16px 24px',
                    textAlign: 'left',
                    fontSize: '10px',
                    fontWeight: '700',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontFamily: 'Inter, Arial, sans-serif'
                  }}>EMAIL</th>
                  <th style={{
                    padding: '16px 24px',
                    textAlign: 'left',
                    fontSize: '10px',
                    fontWeight: '700',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontFamily: 'Inter, Arial, sans-serif'
                  }}>ROLE</th>
                  <th style={{
                    padding: '16px 24px',
                    textAlign: 'left',
                    fontSize: '10px',
                    fontWeight: '700',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontFamily: 'Inter, Arial, sans-serif'
                  }}>STATUS</th>
                  <th style={{
                    padding: '16px 24px',
                    textAlign: 'center',
                    fontSize: '10px',
                    fontWeight: '700',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    fontFamily: 'Inter, Arial, sans-serif'
                  }}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} style={{
                    borderBottom: '1px solid #f8f8f8',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8f8f8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}>
                    <td style={{
                      padding: '16px 24px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#222',
                      fontFamily: 'Inter, Arial, sans-serif'
                    }}>{user.name}</td>
                    <td style={{
                      padding: '16px 24px',
                      fontSize: '14px',
                      color: '#333',
                      fontFamily: 'Inter, Arial, sans-serif'
                    }}>{user.email}</td>
                    <td style={{
                      padding: '16px 24px',
                      fontSize: '14px',
                      color: '#333',
                      fontFamily: 'Inter, Arial, sans-serif'
                    }}>{user.role || 'Staff'}</td>
                    <td style={{
                      padding: '16px 24px'
                    }}>
                      <span style={{
                        padding: '4px 8px',
                        background: user.active !== false ? '#f0fdf4' : '#fee2e2',
                        color: user.active !== false ? '#16a34a' : '#991b1b',
                        fontSize: '11px',
                        fontWeight: '600',
                        textAlign: 'center',
                        fontFamily: 'Inter, Arial, sans-serif',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        border: '1px solid ' + (user.active !== false ? '#bbf7d0' : '#fecaca'),
                        display: 'inline-block'
                      }}>
                        {user.active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{
                      padding: '16px 24px',
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'center'
                    }}>
                      <button 
                        onClick={() => handleToggleStatus(user)}
                        title={user.active !== false ? 'Deactivate User' : 'Activate User'}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #ececec',
                          background: '#f8f8f8',
                          color: '#666',
                          fontSize: '11px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          fontFamily: 'Inter, Arial, sans-serif',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          borderRadius: '0'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#222';
                          e.target.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#f8f8f8';
                          e.target.style.color = '#666';
                        }}
                      >
                        {user.active !== false ? 'DEACTIVATE' : 'ACTIVATE'}
                      </button>
                      <button 
                        onClick={() => handleEdit(user)}
                        title="Edit User"
                        style={{
                          width: '32px',
                          height: '32px',
                          border: '1px solid #ececec',
                          background: '#f8f8f8',
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          fontSize: '12px',
                          borderRadius: '0'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#222';
                          e.target.style.color = '#fff';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#f8f8f8';
                          e.target.style.color = '#666';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={() => handleDelete(user.id)}
                        title="Delete User"
                        style={{
                          width: '32px',
                          height: '32px',
                          border: '1px solid #ececec',
                          background: '#f8f8f8',
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          fontSize: '12px',
                          borderRadius: '0'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#dc2626';
                          e.target.style.color = '#fff';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = '#f8f8f8';
                          e.target.style.color = '#666';
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && (
              <div style={{
                textAlign: 'center',
                padding: '80px 32px',
                color: '#666'
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px'
                }}>👥</div>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#222',
                  margin: '0 0 8px 0',
                  fontFamily: 'Inter, Arial, sans-serif'
                }}>No users found</h3>
                <p style={{
                  fontSize: '14px',
                  margin: '0',
                  fontFamily: 'Inter, Arial, sans-serif'
                }}>Create your first user account to get started.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)',
          padding: '20px',
          overflowY: 'auto'
        }} onClick={(e) => e.target === e.currentTarget && resetForm()}>
          <div style={{
            background: '#fff',
            width: '100%',
            maxWidth: '500px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            margin: 'auto',
            border: '1px solid #ececec'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '24px 24px 0 24px',
              borderBottom: '1px solid #ececec'
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#222',
                margin: '0',
                fontFamily: 'Inter, Arial, sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>{editingUser ? 'EDIT USER' : 'CREATE USER'}</h2>
              <button onClick={resetForm} style={{
                background: 'none',
                border: 'none',
                width: '32px',
                height: '32px',
                fontSize: '18px',
                color: '#666',
                cursor: 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>×</button>
            </div>

            <div style={{
              padding: '24px',
              overflowY: 'auto',
              flex: 1
            }}>
              <form onSubmit={handleSubmit}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <label style={{
                      fontSize: '10px',
                      fontWeight: '700',
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontFamily: 'Inter, Arial, sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaUser style={{ fontSize: '12px' }} /> NAME
                    </label>
                    <input 
                      type="text" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleInputChange} 
                      required 
                      placeholder="Enter full name"
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #ececec',
                        background: '#fff',
                        fontSize: '14px',
                        fontFamily: 'Inter, Arial, sans-serif',
                        transition: 'all 0.2s',
                        borderRadius: '0'
                      }}
                    />
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <label style={{
                      fontSize: '10px',
                      fontWeight: '700',
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontFamily: 'Inter, Arial, sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaEnvelope style={{ fontSize: '12px' }} /> EMAIL
                    </label>
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleInputChange} 
                      required 
                      placeholder="Enter email address"
                      disabled={editingUser}
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #ececec',
                        background: editingUser ? '#f8f8f8' : '#fff',
                        fontSize: '14px',
                        fontFamily: 'Inter, Arial, sans-serif',
                        transition: 'all 0.2s',
                        borderRadius: '0',
                        cursor: editingUser ? 'not-allowed' : 'text'
                      }}
                    />
                    {editingUser && (
                      <div style={{
                        fontSize: '11px',
                        color: '#999',
                        fontFamily: 'Inter, Arial, sans-serif'
                      }}>
                        Email cannot be changed after account creation
                      </div>
                    )}
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <label style={{
                      fontSize: '10px',
                      fontWeight: '700',
                      color: '#666',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      fontFamily: 'Inter, Arial, sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FaUser style={{ fontSize: '12px' }} /> ROLE
                    </label>
                    <select 
                      name="role" 
                      value={formData.role} 
                      onChange={handleInputChange} 
                      required
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #ececec',
                        background: '#fff',
                        fontSize: '14px',
                        fontFamily: 'Inter, Arial, sans-serif',
                        transition: 'all 0.2s',
                        borderRadius: '0',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="Staff">Staff</option>
                      <option value="Admin">Admin</option>
                      <option value="Barista">Barista</option>
                      <option value="Roaster">Roaster</option>
                    </select>
                  </div>

                  {!editingUser && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px'
                    }}>
                      <label style={{
                        fontSize: '10px',
                        fontWeight: '700',
                        color: '#666',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontFamily: 'Inter, Arial, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <FaLock style={{ fontSize: '12px' }} /> PASSWORD
                      </label>
                      <div style={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <input 
                          type={showPassword ? 'text' : 'password'}
                          name="password" 
                          value={formData.password} 
                          onChange={handleInputChange} 
                          required 
                          placeholder="Enter password (min 6 characters)"
                          style={{
                            padding: '10px 40px 10px 12px',
                            border: '1px solid #ececec',
                            background: '#fff',
                            fontSize: '14px',
                            fontFamily: 'Inter, Arial, sans-serif',
                            transition: 'all 0.2s',
                            borderRadius: '0',
                            width: '100%'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: 'absolute',
                            right: '12px',
                            background: 'none',
                            border: 'none',
                            color: '#666',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{
                    background: '#f8f8f8',
                    border: '1px solid #ececec',
                    padding: '16px',
                    fontSize: '12px',
                    color: '#666',
                    fontFamily: 'Inter, Arial, sans-serif',
                    lineHeight: '1.6'
                  }}>
                    <div style={{
                      fontWeight: '700',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>ACCOUNT PERMISSIONS:</div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: '600', marginBottom: '4px', color: '#333' }}>Staff:</div>
                      <ul style={{ margin: '0', paddingLeft: '20px' }}>
                        <li>Access to waste logging system</li>
                        <li>Cannot access admin panel</li>
                        <li>Cannot manage other users</li>
                        <li>Can view and create waste entries</li>
                      </ul>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: '600', marginBottom: '4px', color: '#333' }}>Barista:</div>
                      <ul style={{ margin: '0', paddingLeft: '20px' }}>
                        <li>Access to order management system</li>
                        <li>Can view and process customer orders</li>
                        <li>Access to menu and pricing information</li>
                        <li>Can update order status</li>
                      </ul>
                    </div>
                    
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ fontWeight: '600', marginBottom: '4px', color: '#333' }}>Roaster:</div>
                      <ul style={{ margin: '0', paddingLeft: '20px' }}>
                        <li>Access to roasting schedule and logs</li>
                        <li>Can manage coffee bean inventory</li>
                        <li>Access to quality control system</li>
                        <li>Can update roasting profiles</li>
                      </ul>
                    </div>
                    
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '4px', color: '#333' }}>Admin:</div>
                      <ul style={{ margin: '0', paddingLeft: '20px' }}>
                        <li>Full access to admin panel</li>
                        <li>Can manage all users and settings</li>
                        <li>Access to all reports and analytics</li>
                        <li>Can create/edit/delete products</li>
                        <li>All staff permissions included</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  padding: '20px 0 0 0',
                  borderTop: '1px solid #ececec',
                  marginTop: '24px'
                }}>
                  <button type="button" onClick={resetForm} style={{
                    background: '#fff',
                    border: '1px solid #ececec',
                    color: '#666',
                    padding: '10px 20px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontFamily: 'Inter, Arial, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderRadius: '0'
                  }}>
                    CANCEL
                  </button>
                  <button type="submit" disabled={loading} style={{
                    background: loading ? '#ccc' : '#222',
                    border: '1px solid ' + (loading ? '#ccc' : '#222'),
                    color: '#fff',
                    padding: '10px 20px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    fontFamily: 'Inter, Arial, sans-serif',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderRadius: '0'
                  }}>
                    {loading ? 'SAVING...' : editingUser ? 'UPDATE USER' : 'CREATE USER'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;